/**
 * useFacePresenceGate
 *
 * Hook React que orquestra:
 * - FaceDetector (MediaPipe)
 * - StabilityTracker
 * - TotemStateMachine
 * - GatingPolicy
 *
 * Recebe videoRef externo (o componente controla câmera/stream).
 * Fail-closed: falha de detector/asset/câmera => erro claro + retry.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { FacialObservationResponse } from "@centro-cirurgico/contracts";

import { FaceDetector } from "./face-detector";
import { StabilityTracker } from "./stability-tracker";
import { TotemStateMachine } from "./totem-state-machine";
import { GatingPolicy } from "./gating-policy";
import { observeFace } from "../utils/api";

const DETECT_INTERVAL_MS = 150;
const RETRY_COOLDOWN_MS = 3000;
/** Quadros seguidos sem exatamente um rosto que cancelam a confirmação da saída (~0,6 s). */
const HOLD_MISSES_TO_CANCEL = 4;

export type PresenceGateState =
  | "waiting"
  | "stabilizing"
  | "recognizing"
  | "holding"
  | "identified"
  | "retrying"
  | "error";

/** Confirmação da saída em andamento: o profissional precisa ficar `holdMs` diante do totem. */
export interface ExitHold {
  startedAt: number;
  holdMs: number;
  token: string;
}

export interface UseFacePresenceGateResult {
  state: PresenceGateState;
  message: string;
  result: FacialObservationResponse | null;
  /** Progresso (0..1) da permanência exigida para a saída; `null` fora da confirmação. */
  holdProgress: number | null;
  /** A confirmação da saída foi interrompida (o rosto saiu da frente do totem). */
  holdCancelled: boolean;
  detectorError: string | null;
  isRetryable: boolean;
  retry: () => void;
}

export function useFacePresenceGate(
  roomId: string,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean
): UseFacePresenceGateResult {
  const [state, setState] = useState<PresenceGateState>("waiting");
  const [message, setMessage] = useState("Inicializando reconhecimento");
  const [result, setResult] = useState<FacialObservationResponse | null>(null);
  const [detectorError, setDetectorError] = useState<string | null>(null);
  const [isRetryable, setIsRetryable] = useState(false);
  const [holdProgress, setHoldProgress] = useState<number | null>(null);
  const [holdCancelled, setHoldCancelled] = useState(false);
  const holdRef = useRef<ExitHold | null>(null);
  const holdMissesRef = useRef(0);

  const detectorRef = useRef<FaceDetector | null>(null);
  const trackerRef = useRef<StabilityTracker | null>(null);
  const machineRef = useRef<TotemStateMachine | null>(null);
  const policyRef = useRef<GatingPolicy | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastDetectRef = useRef(0);
  const busyRef = useRef(false);
  const destroyedRef = useRef(false);
  const retryTimeoutRef = useRef<number | null>(null);
  // Espelho de `state` para o loop de detecção. Ler `state` direto faria
  // processFrame/loop/start mudarem a cada transição, e o efeito abaixo
  // descartaria e recriaria o detector (novo contexto WebGL) a cada frame.
  const stateRef = useRef<PresenceGateState>("waiting");

  const resetAll = useCallback(() => {
    trackerRef.current?.reset();
    machineRef.current?.reset();
    policyRef.current?.reset();
    holdRef.current = null;
    holdMissesRef.current = 0;
    setHoldProgress(null);
    setHoldCancelled(false);
    setResult(null);
    setDetectorError(null);
    setIsRetryable(false);
  }, []);

  const setUI = useCallback((newState: PresenceGateState, msg: string) => {
    stateRef.current = newState;
    setState(newState);
    setMessage(msg);
  }, []);

  const handleDetectorError = useCallback(
    (err: unknown, retryable: boolean) => {
      const msg = err instanceof Error ? err.message : String(err);
      setDetectorError(msg);
      setIsRetryable(retryable);
      setUI("error", msg);
    },
    [setUI]
  );

  const scheduleRetryReady = useCallback(() => {
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    const machine = machineRef.current;
    if (!machine || machine.state !== "retrying") return;
    retryTimeoutRef.current = window.setTimeout(() => {
      retryTimeoutRef.current = null;
      if (destroyedRef.current) return;
      const m = machineRef.current;
      if (m && m.state === "retrying") {
        m.transition({ type: "retry_ready" });
        setUI("waiting", "Aguardando rosto");
      }
    }, RETRY_COOLDOWN_MS);
  }, [setUI]);

  const processFrame = useCallback(async () => {
    if (destroyedRef.current || busyRef.current || !enabled) return;
    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

    const detector = detectorRef.current;
    if (!detector || detector.currentStatus.type !== "ready") return;

    const now = performance.now();
    if (now - lastDetectRef.current < DETECT_INTERVAL_MS) return;
    lastDetectRef.current = now;

    try {
      const boxes = await detector.detect(video);
      const tracker = trackerRef.current!;
      const machine = machineRef.current!;
      const policy = policyRef.current!;

      // Confirmação da saída: basta continuar diante do totem (exatamente um rosto) até o fim.
      const hold = holdRef.current;
      if (hold && stateRef.current === "holding") {
        if (boxes.length === 1) {
          holdMissesRef.current = 0;
        } else {
          holdMissesRef.current += 1;
          if (holdMissesRef.current >= HOLD_MISSES_TO_CANCEL) {
            holdRef.current = null;
            holdMissesRef.current = 0;
            setHoldProgress(null);
            setHoldCancelled(true);
            tracker.reset();
            machine.reset();
            policy.reset();
            setUI("waiting", "Saída não registrada");
            return;
          }
        }
        const elapsed = performance.now() - hold.startedAt;
        setHoldProgress(Math.min(1, elapsed / hold.holdMs));
        if (elapsed < hold.holdMs || boxes.length !== 1) return;

        busyRef.current = true;
        holdRef.current = null;
        setUI("recognizing", "Registrando saída");
        try {
          const response = await observeFace({
            roomId,
            imageBase64: captureFrame(video),
            exitToken: hold.token
          });
          setHoldProgress(null);
          setResult(response);
          if (response.status === "recognized" && response.action === "exit") {
            setUI("identified", "Saída registrada");
          } else if (response.status === "recognized" && response.action === "exit_pending") {
            // O servidor não aceitou a confirmação (ex.: demorou demais): pede nova leitura.
            setUI("identified", "Não foi possível confirmar a saída. Afaste-se e tente de novo.");
          } else if (response.status === "recognized" || response.status === "blocked") {
            setUI("identified", response.status === "blocked" ? "Acesso não autorizado. Procure a coordenação." : "Acesso registrado");
          } else {
            tracker.reset();
            machine.reset();
            policy.reset();
            setUI("waiting", "Aguardando rosto");
          }
        } catch (apiError) {
          setHoldProgress(null);
          setUI("error", apiError instanceof Error ? apiError.message : "Falha ao registrar a saída");
          setIsRetryable(true);
        } finally {
          busyRef.current = false;
        }
        return;
      }

      const stabilityEvent = tracker.feed(boxes);

      // Avalia a policy primeiro para decidir se o evento deve afetar a máquina
      const action = policy.onStabilityEvent(stabilityEvent);

      let stateEvent: Parameters<typeof machine.transition>[0] | null = null;
      switch (stabilityEvent.type) {
        case "stable":
          stateEvent = { type: "face_stable" };
          break;
        case "unstable":
          stateEvent = { type: "face_unstable" };
          break;
        case "absent":
          stateEvent = { type: "face_absent" };
          break;
        case "multiple":
          stateEvent = { type: "face_multiple" };
          break;
      }

      if (stateEvent) {
        const prevState = machine.state;
        let shouldTransition = true;

        // Em identified, só rearma se a policy indicar nova face (call_api)
        if (prevState === "identified" && stabilityEvent.type === "stable") {
          shouldTransition = action.type === "call_api";
        }

        // Durante recognizing, ignora face_stable/unstable/multiple
        if (prevState === "recognizing" && (stabilityEvent.type === "stable" || stabilityEvent.type === "unstable" || stabilityEvent.type === "multiple")) {
          shouldTransition = false;
        }

        if (shouldTransition) {
          machine.transition(stateEvent);
          if (prevState === "identified" && machine.state !== "identified") {
            setResult(null);
            setHoldCancelled(false);
          }
          if (prevState !== "retrying" && machine.state === "retrying") {
            scheduleRetryReady();
          }
        }
      }

      switch (machine.state) {
        case "waiting":
          if (stateRef.current !== "waiting" && stateRef.current !== "error") setUI("waiting", "Aguardando rosto");
          break;
        case "stabilizing":
          setUI("stabilizing", "Mantenha-se parado");
          break;
        case "recognizing":
          setUI("recognizing", "Verificando acesso");
          break;
        case "identified":
          break;
        case "retrying":
          setUI("retrying", "Tentando novamente...");
          break;
      }

      // API só dispara quando a máquina está efetivamente em recognizing
      if (stabilityEvent.type === "stable" && machine.state === "recognizing") {
        if (action.type === "call_api") {
          busyRef.current = true;
          policy.onApiCallStarted(action.box);
          setHoldCancelled(false);

          let imageBase64: string;
          try {
            imageBase64 = captureFrame(video);
          } catch {
            policy.onApiCallFinished();
            busyRef.current = false;
            return;
          }

          try {
            const response = await observeFace({ roomId, imageBase64 });

            policy.onApiResult(response);

            if (response.status === "recognized" && response.action === "exit_pending" && response.exitToken) {
              // Entrada aberta: a saída só é gravada se o profissional continuar diante do totem.
              machine.transition({ type: "recognize_success", recorded: false });
              holdRef.current = {
                startedAt: performance.now(),
                holdMs: response.holdMs ?? 3000,
                token: response.exitToken
              };
              holdMissesRef.current = 0;
              setHoldProgress(0);
              setResult(response);
              setUI("holding", "Fique em frente ao totem para registrar a saída");
            } else if (response.status === "recognized") {
              machine.transition({ type: "recognize_success", recorded: response.recorded });
              setResult(response);
              setUI("identified", identifiedMessage(response));
            } else if (response.status === "blocked") {
              // 4.5: inativo ou fora da validade — rosto identificado, acesso negado.
              machine.transition({ type: "recognize_success", recorded: response.recorded });
              setResult(response);
              setUI("identified", "Acesso não autorizado. Procure a coordenação.");
            } else if (response.status === "unrecognized") {
              machine.transition({ type: "recognize_fail" });
              setResult(response);
              setUI("retrying", "Rosto não reconhecido");
              scheduleRetryReady();
            } else {
              machine.transition({ type: "face_absent" });
              setResult(null);
            }
          } catch (apiError) {
            policy.onApiCallFinished();
            setUI(
              "error",
              apiError instanceof Error ? apiError.message : "Falha ao verificar acesso"
            );
            setIsRetryable(true);
          } finally {
            busyRef.current = false;
          }
        }
      } else if (stabilityEvent.type === "absent") {
        if (machine.state === "waiting") {
          setUI("waiting", "Aguardando rosto");
        }
      }
    } catch (detectError) {
      handleDetectorError(detectError, true);
    }
  }, [roomId, enabled, setUI, handleDetectorError, scheduleRetryReady, videoRef]);

  const loop = useCallback(() => {
    if (destroyedRef.current) return;
    void processFrame();
    rafRef.current = requestAnimationFrame(loop);
  }, [processFrame]);

  const start = useCallback(async () => {
    resetAll();
    destroyedRef.current = false;

    detectorRef.current = new FaceDetector();
    trackerRef.current = new StabilityTracker();
    machineRef.current = new TotemStateMachine({ retryCooldownMs: RETRY_COOLDOWN_MS });
    policyRef.current = new GatingPolicy({ retryCooldownMs: RETRY_COOLDOWN_MS });

    try {
      await detectorRef.current.initialize();
      setUI("waiting", "Aguardando rosto");
      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      handleDetectorError(err, true);
    }
  }, [resetAll, setUI, handleDetectorError, loop]);

  const retry = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    detectorRef.current?.dispose();
    resetAll();
    void start();
  }, [resetAll, start]);

  useEffect(() => {
    if (!enabled) {
      destroyedRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      detectorRef.current?.dispose();
      return;
    }
    void start();
    return () => {
      destroyedRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      detectorRef.current?.dispose();
    };
  }, [enabled, start]);

  return {
    state,
    message,
    result,
    holdProgress,
    holdCancelled,
    detectorError,
    isRetryable,
    retry,
  };
}

/** Quadro atual da câmera em JPEG (data URL). */
function captureFrame(video: HTMLVideoElement): string {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth || 960;
  canvas.height = video.videoHeight || 540;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponível.");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

function identifiedMessage(response: FacialObservationResponse): string {
  switch (response.action) {
    case "entry":
      return "Entrada registrada";
    case "exit":
      return "Saída registrada";
    case "repeat_entry":
      return "Entrada já registrada";
    case "repeat_exit":
      return "Saída já registrada";
    default:
      return response.recorded ? "Acesso registrado" : "Acesso já registrado";
  }
}
